import { cn, cva, type VariantProps } from 'noddi-ui-common';
import { commonTexts, treadDepthTone, type TreadDepthThresholds } from 'noddi-util';
import { TireDimensionLabel } from '../Tire/TireDimensionLabel';
import {
  type TireAxleDimensions,
  type TreadDepthAxle,
  type TreadDepthCorner,
  type TreadDepthGridSize,
  type TreadDepthValues
} from './treadDepth';

export type TreadDepthGridTranslations = {
  frontAxleShort: string;
  rearAxleShort: string;
  leftFront: string;
  rightFront: string;
  leftRear: string;
  rightRear: string;
  noMeasurements: string;
};

type Props = {
  /** Tread depth in mm per wheel position. Missing positions render as blanks. */
  values: TreadDepthValues;
  /**
   * Legal and recommended minimums for this wheel set, straight off the API record. Null where the
   * country has no tread depth regulation or the source carries none: readings then show without a tone.
   */
  thresholds: TreadDepthThresholds | null;
  /** `small` fits a table row, `medium` a detail sheet. */
  size?: TreadDepthGridSize;
  /**
   * Tire size column after the grid. One size spans both rows when front and rear
   * match; a staggered fitment gets one per row, with the rear in amber so the
   * odd one out is seen before tires are ordered for the wrong axle.
   */
  dimensions?: TireAxleDimensions;
  className?: string;
  translations?: TreadDepthGridTranslations;
};

const AXLE_ROWS = [
  { axle: 'front', corners: ['frontLeft', 'frontRight'] },
  { axle: 'rear', corners: ['rearLeft', 'rearRight'] }
] as const satisfies ReadonlyArray<{ axle: TreadDepthAxle; corners: readonly TreadDepthCorner[] }>;

const cellVariants = cva('flex items-center justify-center font-semibold tabular-nums', {
  variants: {
    tone: {
      good: 'bg-success20 text-successDark',
      soon: 'bg-warning50 text-warningDark',
      worn: 'bg-danger50 text-dangerDark',
      // Nothing to grade against: the reading alone, no traffic light.
      neutral: 'bg-muted text-foreground',
      // A wheel with no measurement is absent, not zero — no fill, no number.
      blank: 'text-gray-300'
    },
    size: {
      small: 'h-[18px] rounded-[4px] text-xs',
      medium: 'h-[26px] rounded-[5px] text-sm'
    }
  }
});

const cornerWidthVariants = cva('', {
  variants: {
    size: {
      small: 'w-8',
      medium: 'w-[54px]'
    }
  }
});

/** One value for the whole axle spans both corner cells and the gap between them. */
const axleWidthVariants = cva('', {
  variants: {
    size: {
      small: 'w-[67px]',
      medium: 'w-28'
    }
  }
});

const gapVariants = cva('flex', {
  variants: {
    size: {
      small: 'gap-[3px]',
      medium: 'gap-1'
    }
  }
});

/** Row height of one axle, shared by everything that sits on a row so the rows line up. */
const rowHeightVariants = cva('', {
  variants: {
    size: {
      small: 'h-[18px]',
      medium: 'h-[26px]'
    }
  }
});

const axleLabelVariants = cva('flex w-[14px] shrink-0 items-center text-xs font-semibold text-muted-foreground');

const dimensionColumnVariants = cva('ml-1 border-l border-border pl-2 whitespace-nowrap tabular-nums', {
  variants: {
    size: {
      small: 'text-xs',
      medium: 'text-sm'
    }
  }
});

type CellSize = NonNullable<VariantProps<typeof cellVariants>['size']>;

/**
 * The one way backoffice shows a car's tread depth: front row above rear row,
 * left wheel before right, traffic-light chips, with its own F / R gutter so it
 * reads on its own next to another season's grid, and the tire size after it
 * when known. Pass the size through `dimensions` rather than rendering it beside
 * the grid, so every page lines size and depth up the same way. Colors come from the wheel set's legal and recommended
 * minimums, so summer and winter band differently without the caller knowing
 * the regulations.
 *
 * Two-wheelers that measure a whole axle instead of each corner get one wide
 * cell for that row.
 */
export const TreadDepthGrid = ({ values, thresholds, size = 'small', dimensions, className, translations }: Props) => {
  const t = translations ?? commonTexts.wheelMeasurements;
  const hasAnyValue = AXLE_ROWS.some(
    ({ axle, corners }) => values[axle] != null || corners.some((corner) => values[corner] != null)
  );

  const cornerLabels: Record<TreadDepthCorner, string> = {
    frontLeft: t.leftFront,
    frontRight: t.rightFront,
    rearLeft: t.leftRear,
    rearRight: t.rightRear
  };

  const axleLabels: Record<TreadDepthAxle, string> = {
    front: t.frontAxleShort,
    rear: t.rearAxleShort
  };

  const grid = (
    <div className={cn(gapVariants({ size }), 'flex-col', !dimensions && className)}>
      {AXLE_ROWS.map(({ axle, corners }, rowIndex) => {
        const axleValue = values[axle];
        const hasCornerValue = corners.some((corner) => values[corner] != null);

        return (
          <div key={axle} className={cn(gapVariants({ size }), 'items-center')}>
            <span className={cn(axleLabelVariants(), rowHeightVariants({ size }))}>{axleLabels[axle]}</span>
            {axleValue != null && !hasCornerValue ? (
              <Cell
                valueMm={axleValue}
                thresholds={thresholds}
                size={size}
                label={axleLabels[axle]}
                className={axleWidthVariants({ size })}
              />
            ) : (
              corners.map((corner) => (
                <Cell
                  key={corner}
                  valueMm={values[corner]}
                  thresholds={thresholds}
                  size={size}
                  label={cornerLabels[corner]}
                  // The first cell carries the dash when nothing is measured, so an
                  // unmeasured season reads as one "–" instead of four empty boxes.
                  placeholder={!hasAnyValue && rowIndex === 0 && corner === corners[0] ? t.noMeasurements : undefined}
                  className={cornerWidthVariants({ size })}
                />
              ))
            )}
          </div>
        );
      })}
    </div>
  );

  if (!dimensions?.front && !dimensions?.rear) {
    return grid;
  }

  return (
    <div className={cn('flex items-stretch', className)}>
      {grid}
      <DimensionColumn dimensions={dimensions} size={size} />
    </div>
  );
};

const DimensionColumn = ({ dimensions: { front, rear }, size }: { dimensions: TireAxleDimensions; size: CellSize }) => {
  if (front === rear) {
    return (
      <span className={cn(dimensionColumnVariants({ size }), 'flex items-center')}>
        <TireDimensionLabel value={front} />
      </span>
    );
  }

  const isStaggered = !!front && !!rear;

  return (
    <div className={cn(dimensionColumnVariants({ size }), gapVariants({ size }), 'flex-col')}>
      <span className={cn(rowHeightVariants({ size }), 'flex items-center')}>
        {front ? <TireDimensionLabel value={front} /> : '–'}
      </span>
      <span
        className={cn(
          rowHeightVariants({ size }),
          'flex items-center',
          isStaggered && 'font-semibold text-warningDark'
        )}
      >
        {rear ? <TireDimensionLabel value={rear} /> : '–'}
      </span>
    </div>
  );
};

type CellProps = {
  valueMm: number | undefined;
  thresholds: TreadDepthThresholds | null;
  size: CellSize;
  label: string;
  placeholder?: string;
  className?: string;
};

const Cell = ({ valueMm, thresholds, size, label, placeholder, className }: CellProps) => {
  if (valueMm == null) {
    return (
      <span
        className={cn(cellVariants({ tone: 'blank', size }), className)}
        aria-label={placeholder ? `${label}: ${placeholder}` : undefined}
      >
        {placeholder ? '–' : ''}
      </span>
    );
  }

  return (
    <span
      className={cn(
        cellVariants({ tone: thresholds ? treadDepthTone(valueMm, thresholds) : 'neutral', size }),
        className
      )}
      aria-label={`${label}: ${valueMm.toFixed(1)} mm`}
    >
      {valueMm.toFixed(1)}
    </span>
  );
};
